import React, { useState, useEffect, useCallback, useRef } from 'react';
import './FeatureTour.css';

/*
  Tour de funcionalidades nuevas (coachmarks) — sin librerías externas.
  Resalta elementos del DOM con un spotlight + cartelito, paso a paso.

  Props:
    - storageKey: clave de localStorage (incluí versión, ej. "ts_tour_v1").
        Si ya está seteada, el tour no se muestra. Subí la versión para volver a mostrarlo.
    - steps: [{ selector, title, body }]. Solo se usan los pasos cuyo elemento existe.
    - run: (opcional) si es true, fuerza el tour ignorando el localStorage (para "ver de nuevo").
    - onClose: (opcional) callback al terminar/saltar.
*/
const PADDING = 8;
const TOOLTIP_W = 320;

const FeatureTour = ({ storageKey = 'ts_tour_v1', steps = [], run = false, onClose }) => {
  const [activeSteps, setActiveSteps] = useState([]);
  const [index, setIndex] = useState(0);
  const [rect, setRect] = useState(null);
  const [visible, setVisible] = useState(false);
  const startedRef = useRef(false);

  // Calcula los pasos disponibles (cuyo target existe) y arranca el tour.
  const start = useCallback(() => {
    const disponibles = steps.filter(s => document.querySelector(s.selector));
    if (disponibles.length === 0) return false;
    setActiveSteps(disponibles);
    setIndex(0);
    setVisible(true);
    return true;
  }, [steps]);

  // Arranque: solo la primera vez (o si run=true). Reintenta unas veces porque el
  // contenido del Inicio puede tardar un toque en pintarse.
  useEffect(() => {
    if (startedRef.current) return;
    const yaVisto = localStorage.getItem(storageKey);
    if (yaVisto && !run) return;

    let intentos = 0;
    const id = setInterval(() => {
      intentos += 1;
      if (start() || intentos >= 12) {
        startedRef.current = true;
        clearInterval(id);
      }
    }, 350);
    return () => clearInterval(id);
  }, [start, storageKey, run]);

  const cerrar = useCallback(() => {
    localStorage.setItem(storageKey, '1');
    setVisible(false);
    if (onClose) onClose();
  }, [storageKey, onClose]);

  const siguiente = useCallback(() => {
    setIndex(prev => {
      if (prev + 1 >= activeSteps.length) { cerrar(); return prev; }
      return prev + 1;
    });
  }, [activeSteps.length, cerrar]);

  const anterior = () => setIndex(prev => Math.max(0, prev - 1));

  // Reposiciona el spotlight sobre el target del paso actual.
  const actualizarRect = useCallback(() => {
    const step = activeSteps[index];
    if (!step) return;
    const el = document.querySelector(step.selector);
    if (!el) { siguiente(); return; }
    el.scrollIntoView({ block: 'center', behavior: 'smooth' });
    const r = el.getBoundingClientRect();
    setRect({ top: r.top, left: r.left, width: r.width, height: r.height });
  }, [activeSteps, index, siguiente]);

  useEffect(() => {
    if (!visible) return;
    actualizarRect();
    const onChange = () => actualizarRect();
    window.addEventListener('resize', onChange);
    window.addEventListener('scroll', onChange, true);
    return () => {
      window.removeEventListener('resize', onChange);
      window.removeEventListener('scroll', onChange, true);
    };
  }, [visible, index, actualizarRect]);

  if (!visible || !rect) return null;

  const step = activeSteps[index];
  const esUltimo = index === activeSteps.length - 1;

  // Posición del cartelito: debajo del target si entra, si no arriba. Centrado y acotado al viewport.
  const espacioAbajo = window.innerHeight - (rect.top + rect.height);
  const abajo = espacioAbajo > 200;
  const tooltipTop = abajo ? rect.top + rect.height + PADDING + 10 : rect.top - PADDING - 10;
  let tooltipLeft = rect.left + rect.width / 2 - TOOLTIP_W / 2;
  tooltipLeft = Math.max(12, Math.min(tooltipLeft, window.innerWidth - TOOLTIP_W - 12));

  return (
    <div className="ftour">
      {/* Spotlight: recorta el target y oscurece todo lo demás vía box-shadow */}
      <div
        className="ftour__spot"
        style={{
          top: rect.top - PADDING,
          left: rect.left - PADDING,
          width: rect.width + PADDING * 2,
          height: rect.height + PADDING * 2,
        }}
      />
      {/* Cartelito */}
      <div
        className="ftour__tip"
        style={{
          top: tooltipTop,
          left: tooltipLeft,
          width: TOOLTIP_W,
          transform: abajo ? 'none' : 'translateY(-100%)',
        }}
      >
        <div className="ftour__badge">Nuevo</div>
        <h3 className="ftour__title">{step.title}</h3>
        <p className="ftour__body">{step.body}</p>
        <div className="ftour__foot">
          <span className="ftour__count">{index + 1} / {activeSteps.length}</span>
          <div className="ftour__actions">
            <button className="ftour__skip" onClick={cerrar}>Saltar</button>
            {index > 0 && (
              <button className="ftour__back" onClick={anterior}>Anterior</button>
            )}
            <button className="ftour__next" onClick={siguiente}>
              {esUltimo ? 'Listo' : 'Siguiente'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FeatureTour;
